// Screen registry — the bespoke bodies a journey step can name.
//
// Most steps are a `form` and render through <JourneyStep/>; nothing here is
// needed for them. A step with its own markup (a plan list, a document upload,
// a summary) names a key as `screen` in its config, and App.js mounts whatever
// was registered under that key at the step's path. The concrete pages are
// registered in src/screens.js — this module stays free of page imports so the
// form engine and shell can be type-checked without dragging every page in.
import React from "react";
import JourneyStep from "./JourneyStep";

/** @type {Record<string, React.ComponentType<*>>} */
const SCREENS = { "journey-step": JourneyStep };

/**
 * @param {string} key
 * @param {React.ComponentType<*>} component
 */
export const registerScreen = (key, component) => {
  SCREENS[key] = component;
};

/** @param {Record<string, React.ComponentType<*>>} map */
export const registerScreens = (map) => {
  for (const [key, component] of Object.entries(map)) registerScreen(key, component);
};

/** @param {string | null | undefined} key */
export const getScreen = (key) => (key ? SCREENS[key] : undefined) || null;

export const registeredScreens = () => Object.keys(SCREENS).sort();

/**
 * The element for a derived route: the named screen with its props, or the
 * generic form step when no screen is named. A named screen nobody registered
 * is a config mistake, so it fails loudly in development rather than
 * rendering the wrong thing.
 *
 * @param {{ path: string, screen: string | null, screenProps: Record<string, *> }} route
 */
export const elementFor = (route) => {
  const Screen = getScreen(route.screen);
  if (route.screen && !Screen) {
    const message = `Route ${route.path} names screen "${route.screen}", which is not registered`;
    if (process.env.NODE_ENV !== "production") throw new Error(message);
    // eslint-disable-next-line no-console
    console.error(message);
  }
  const Component = Screen || JourneyStep;
  return <Component {...(route.screenProps || {})} />;
};

export default SCREENS;
